export const personaDecisionRiskLabelMap: Record<
  string,
  {
    label: string;
    category: string;
    description: string;
    reasons: { reason_slug: string; description: string }[];
  }
> = {
  id_account_comparison: {
    label: "Account comparison",
    category: "Fraud",
    description:
      "Compare if government ID details (e.g. name, birthdate) are consistent with account details.",
    reasons: [
      {
        reason_slug: "face_mismatch",
        description:
          "Face is inconsistent with the selfie found on the account.",
      },
      {
        reason_slug: "property_conflict",
        description: "ID properties are inconsistent with the account details.",
      },
      {
        reason_slug: "missing_properties",
        description: "Missing properties for account comparison.",
      },
      {
        reason_slug: "no_account",
        description: "No account associated with this verification.",
      },
      {
        reason_slug: "unsupported_comparison_language",
        description:
          "Comparison attributes include characters from an unsupported language.",
      },
    ],
  },
  id_age_comparison: {
    label: "Age comparison",
    category: "User action required",
    description:
      "Detect if the age listed on the ID meets the age restriction.",
    reasons: [
      {
        reason_slug: "failed_requirement",
        description: "ID details do not meet age requirements.",
      },
      {
        reason_slug: "missing_birthdate",
        description: "The birthdate is missing.",
      },
      {
        reason_slug: "no_configuration",
        description:
          "The verification does not have an age comparison configuration.",
      },
    ],
  },
  id_age_inconsistency_detection: {
    label: "Portrait age",
    category: "Fraud",
    description:
      "Detect if the age listed on the ID is different than the age estimated from the face portrait.",
    reasons: [
      {
        reason_slug: "inconsistent_age",
        description:
          "The age listed on the ID appears to be different than the age estimated from the face portrait.",
      },
      {
        reason_slug: "missing_age_range",
        description: "The age range is missing.",
      },
      {
        reason_slug: "missing_birthdate",
        description: "The birth date is missing.",
      },
      {
        reason_slug: "missing_face",
        description: "The ID has no face.",
      },
      {
        reason_slug: "missing_issuing_date",
        description: "The issue date is missing.",
      },
    ],
  },
  id_attribute_comparison: {
    label: "Attribute comparison",
    category: "Fraud",
    description:
      "Compare if government ID details (e.g. name, birthdate) are consistent with provided values. Generally used for verifications created outside an inquiry.",
    reasons: [
      {
        reason_slug: "property_conflict",
        description: "ID properties are inconsistent with the provided values.",
      },
      {
        reason_slug: "missing_properties",
        description: "Claimed attributes like name or birthdate are missing.",
      },
      {
        reason_slug: "unsupported_comparison_language",
        description:
          "Claimed or extracted attributes include characters from an unsupported language.",
      },
    ],
  },
  id_barcode_detection: {
    label: "Barcode",
    category: "User action required",
    description: "Detect if there is a barcode on the ID.",
    reasons: [
      {
        reason_slug: "back_missing",
        description: "Unsuccessful detection of the back of the ID.",
      },
      {
        reason_slug: "barcode_not_decoded",
        description: "The barcode on the ID cannot be decoded.",
      },
      {
        reason_slug: "barcode_not_detected",
        description: "The barcode on the ID is not found.",
      },
      {
        reason_slug: "barcode_not_required",
        description: "The barcode was not collected.",
      },
      {
        reason_slug: "unsupported_country",
        description: "This check is not currently supported for this country.",
      },
    ],
  },
  id_barcode_inconsistency_detection: {
    label: "Barcode inconsistency",
    category: "Fraud",
    description: "Detect inconsistencies in the barcode on the ID.",
    reasons: [
      {
        reason_slug: "field_invalid",
        description: "The barcode has fields in invalid formats.",
      },
      {
        reason_slug: "format_invalid",
        description: "The barcode does not follow ID specifications.",
      },
      {
        reason_slug: "issuer_invalid",
        description: "The barcode specifies an issuer that is invalid.",
      },
      {
        reason_slug: "issuer_mismatch",
        description: "The barcode has inconsistent issuer information.",
      },
      {
        reason_slug: "metadata_invalid",
        description:
          "Barcode metadata is inconsistent with genuine IDs from the provided issuer.",
      },
      {
        reason_slug: "required_field_missing",
        description: "The barcode is missing required fields.",
      },
      {
        reason_slug: "revision_invalid",
        description:
          "Barcode revision is inconsistent with genuine IDs from the provided issuer.",
      },
      {
        reason_slug: "back_missing",
        description: "Unsuccessful detection of the back of the ID.",
      },
      {
        reason_slug: "barcode_not_decoded",
        description: "The barcode on the ID cannot be decoded.",
      },
      {
        reason_slug: "barcode_not_detected",
        description: "The barcode on the ID is not found.",
      },
      {
        reason_slug: "unsupported_country",
        description: "This check is not currently supported for this country.",
      },
    ],
  },
  id_color_inconsistency_detection: {
    label: "Color",
    category: "Fraud",
    description:
      "Detect if the colors on the ID are different than the expected colors.",
    reasons: [
      {
        reason_slug: "image_is_monochrome",
        description:
          "The colors detected on the ID appear to be monochromatic.",
      },
    ],
  },
  id_compromised_detection: {
    label: "Compromised submission",
    category: "Fraud",
    description:
      "Detect if the submission can be found from a publicly available source (e.g. Google Images).",
    reasons: [
      {
        reason_slug: "fraudulent",
        description: "The ID is reported as fraudulent.",
      },
      {
        reason_slug: "publicly_available",
        description: "The ID is likely a sample ID or publicly available.",
      },
      {
        reason_slug: "sample_document",
        description: "The ID contains a word that indicates it is a sample.",
      },
    ],
  },
  id_corner_detection: {
    label: "BETA Corner detection",
    category: "User action required",
    description:
      "Detect if the submitted ID is fully in frame with all four corners visible.",
    reasons: [
      {
        reason_slug: "insufficient_padding",
        description: "The ID is too close to the edge of the image.",
      },
      {
        reason_slug: "unidentified",
        description: "The ID could not be identified.",
      },
      {
        reason_slug: "unsupported_country",
        description: "The country is not supported by this check.",
      },
      {
        reason_slug: "unsupported_document_type",
        description: "The type of ID is not supported by this check.",
      },
    ],
  },
  id_damaged_detection: {
    label: "BETA Damaged detection",
    category: "Fraud",
    description:
      "Detect physical damages on the ID that render it invalid for verification.",
    reasons: [
      {
        reason_slug: "holepunch_detected",
        description: "A holepunch was detected on the ID.",
      },
    ],
  },
  id_disallowed_country_detection: {
    label: "Allowed country",
    category: "User action required",
    description:
      "Detect if the country of the ID is not allowed based on template requirements.",
    reasons: [
      {
        reason_slug: "no_country",
        description: "No country was extracted.",
      },
      {
        reason_slug: "not_allowed_by_configuration",
        description:
          "The country of the ID is not allowed based on template requirements.",
      },
      {
        reason_slug: "selected_country_mismatch",
        description:
          "The country of the ID does not match the country selected by the user.",
      },
      {
        reason_slug: "no_country_configuration",
        description: "All countries are allowed.",
      },
    ],
  },
  id_disallowed_type_detection: {
    label: "Allowed ID type",
    category: "User action required",
    description:
      "Detect if the ID type (e.g. driver license) submitted does not match the ID type the user selected.",
    reasons: [
      {
        reason_slug: "disallowed_designation",
        description:
          "The ID has a designation that is not allowed based on template requirements.",
      },
      {
        reason_slug: "not_allowed_by_configuration",
        description:
          "The ID type is not allowed based on template requirements.",
      },
      {
        reason_slug: "restricted_type",
        description:
          "The ID type cannot be collected due to federal compliance requirements.",
      },
      {
        reason_slug: "type_mismatch",
        description:
          "The ID type detected does not match the ID type the user selected.",
      },
      {
        reason_slug: "type_not_detected",
        description: "The ID type of the submission could not be identified.",
      },
      {
        reason_slug: "unrestricted",
        description: "There are no restrictions on ID type.",
      },
    ],
  },
  id_double_side_detection: {
    label: "Double side",
    category: "User action required",
    description:
      "Detect if one side of the ID is submitted as both front and back.",
    reasons: [
      {
        reason_slug: "side_submitted_twice",
        description:
          "The back and front submissions of the ID are of the same side.",
      },
      {
        reason_slug: "single_sided",
        description: "The provided ID is expected to only have one side",
      },
    ],
  },
  id_electronic_replica_detection: {
    label: "Electronic replica",
    category: "Fraud",
    description:
      "Detect if the submission is a replica of an image presented on another electronic device or screen.",
    reasons: [
      {
        reason_slug: "digital_text_found",
        description:
          "The submission may contain digital text which is often found on a replica.",
      },
      {
        reason_slug: "emulator_detected",
        description: "The submission may have been submitted on an emulator.",
      },
      {
        reason_slug: "photocopy_found",
        description: "The submission may be a replica of a photocopy.",
      },
      {
        reason_slug: "replica_detected",
        description: "The submission may be a generic replica.",
      },
      {
        reason_slug: "rooted_device",
        description:
          "The submission may have been submitted on a rooted device.",
      },
      {
        reason_slug: "screenshot_found",
        description: "The submission may be a replica of a screenshot.",
      },
      {
        reason_slug: "virtual_camera_detected",
        description:
          "The submission may have been submitted using a virtual camera.",
      },
    ],
  },
  id_entity_detection: {
    label: "Government ID",
    category: "User action required",
    description: "Detect if there is a government ID in the submission.",
    reasons: [
      {
        reason_slug: "cannot_verify_id",
        description:
          "This submission does not appear to include a government ID.",
      },
    ],
  },
  id_expired_detection: {
    label: "Expiration",
    category: "User action required",
    description: "Detect if the ID is expired.",
    reasons: [
      {
        reason_slug: "expired",
        description: "The ID is expired.",
      },
      {
        reason_slug: "no_expiration_date",
        description: "The expiration date is missing.",
      },
    ],
  },
  id_extracted_properties_detection: {
    label: "Extracted properties",
    category: "User action required",
    description: "Detect if template-required ID details are extracted.",
    reasons: [
      {
        reason_slug: "required_properties_absent_on_id",
        description:
          "The missing required properties are not present on the ID.",
      },
      {
        reason_slug: "property_not_extracted",
        description: "Required ID field(s) were not extracted.",
      },
      {
        reason_slug: "name_partially_extracted",
        description:
          "First name and last name were required, but only one was extracted.",
      },
      {
        reason_slug: "no_required_properties",
        description:
          "The template is not configured to require extraction of certain properties.",
      },
    ],
  },
  id_extraction_inconsistency_detection: {
    label: "Extraction inconsistency",
    category: "Fraud",
    description:
      "Detect if the details extracted from the front of the ID are different than the details extracted from the barcode.",
    reasons: [
      {
        reason_slug: "property_mismatch",
        description:
          "Details extracted from the front of the ID are different than the details extracted from the barcode.",
      },
      {
        reason_slug: "back_missing",
        description: "Unsuccessful detection of the back of the ID.",
      },
      {
        reason_slug: "missing_properties",
        description: "Barcode is missing properties to compare.",
      },
      {
        reason_slug: "unsupported_country",
        description: "This check is not currently supported for this country.",
      },
    ],
  },
  id_fabrication_detection: {
    label: "Fabrication",
    category: "Fraud",
    description:
      "Detect if the ID is likely to be fabricated, based on its format and issuing entity.",
    reasons: [
      {
        reason_slug: "fabricated_id_version",
        description:
          "The submitted ID does not match a known in-circulation format of that ID type.",
      },
      {
        reason_slug: "camouflage_passport",
        description:
          "The submitted ID is issued by an unrecognized or invalid government entity.",
      },
    ],
  },
  id_glare_detection: {
    label: "BETA Glare",
    category: "User action required",
    description: "Detect if the submission has glare.",
    reasons: [
      {
        reason_slug: "too_much_glare",
        description: "The submission has glare.",
      },
    ],
  },
  id_inconsistent_repeat_detection: {
    label: "Inconsistent repeat",
    category: "Fraud",
    description:
      "Detect if either the ID details or face portrait match that of a previously submitted ID within the last year.",
    reasons: [
      {
        reason_slug: "inconsistent_details",
        description:
          "The face portrait matches that of a previously submitted ID with different details.",
      },
      {
        reason_slug: "inconsistent_face",
        description:
          "The ID details matches that of a previously submitted ID with a different face portrait.",
      },
      {
        reason_slug: "low_quality_portrait",
        description: "Portrait quality is too low for comparison.",
      },
      {
        reason_slug: "no_portrait",
        description: "The face portrait is missing.",
      },
      {
        reason_slug: "not_enabled",
        description: "Inconsistent repeat is not enabled.",
      },
    ],
  },
  id_inquiry_comparison: {
    label: "Inquiry comparison",
    category: "Fraud",
    description:
      "Compare if ID details like name or birthdate are inconsistent between different submission attempts.",
    reasons: [
      {
        reason_slug: "names_swapped",
        description:
          "The claimed first name matches the extracted last name and vice versa.",
      },
      {
        reason_slug: "property_conflict",
        description:
          "ID details like name or birthdate are inconsistent between different submission attempts.",
      },
      {
        reason_slug: "missing_properties",
        description: "Inquiry details like name or birthdate are missing.",
      },
      {
        reason_slug: "no_inquiry",
        description: "No associated inquiry.",
      },
      {
        reason_slug: "unsupported_comparison_language",
        description:
          "Comparison attributes include characters from an unsupported language.",
      },
    ],
  },
  id_mrz_detection: {
    label: "MRZ Detected",
    category: "User action required",
    description: "Detect if there is encoded, machine-readable text on the ID.",
    reasons: [
      {
        reason_slug: "mrz_incomplete",
        description:
          "The full area of the encoded, machine-readable text on the ID was not detected.",
      },
      {
        reason_slug: "mrz_not_found",
        description:
          "The encoded, machine-readable text on the ID is not found.",
      },
    ],
  },
  id_mrz_inconsistency_detection: {
    label: "MRZ Inconsistency",
    category: "Fraud",
    description:
      "Detect if the encoded, machine-readable text on the ID is well-formed.",
    reasons: [
      {
        reason_slug: "mrz_checksum_invalid",
        description:
          "The encoded, machine-readable text on the ID has invalid checksum(s).",
      },
      {
        reason_slug: "mrz_not_found",
        description:
          "The encoded, machine-readable text on the ID is not found.",
      },
      {
        reason_slug: "mrz_not_verifiable",
        description:
          "The encoded, machine-readable text on the ID is present but not verifiable (e.g. because it may be partially obscured).",
      },
    ],
  },
  id_number_format_inconsistency_detection: {
    label: "ID number format inconsistency",
    category: "Fraud",
    description:
      "Detect if the ID number format is well-formed based on issuing authority rules for certain countries.",
    reasons: [
      {
        reason_slug: "incorrect_format",
        description: "The ID number has an invalid format.",
      },
      {
        reason_slug: "invalid_checksum",
        description: "The ID number has an invalid checksum.",
      },
      {
        reason_slug: "invalid_encoded_data",
        description:
          "Data encoded in the ID number does not match data extracted from the ID.",
      },
      {
        reason_slug: "unsupported_country",
        description: "This check is not currently supported for this country.",
      },
    ],
  },
  id_paper_detection: {
    label: "BETA Paper detection",
    category: "Fraud",
    description: "Detect if the ID is a paper copy",
    reasons: [
      {
        reason_slug: "paper_id_detected",
        description: "The submitted ID is printed on a piece of paper.",
      },
      {
        reason_slug: "expected_paper_id_type",
        description: "The submitted ID type matches a known paper ID type.",
      },
    ],
  },
  id_physical_tamper_detection: {
    label: "BETA ID physical tampering",
    category: "Fraud",
    description: "Detect if the submission is physically tampered.",
    reasons: [
      {
        reason_slug: "tamper_detected",
        description: "The submission appears to have been tampered physically.",
      },
    ],
  },
  id_po_box_detection: {
    label: "PO box",
    category: "User action required",
    description:
      "Detect if the address is a PO box. Note that address parts may be extracted both visually and from the barcode / MRZ.",
    reasons: [
      {
        reason_slug: "address_is_hc_box",
        description: "The address is a HC box.",
      },
      {
        reason_slug: "address_is_po_box",
        description: "The address is a PO box.",
      },
      {
        reason_slug: "missing_address",
        description: "No address was submitted.",
      },
    ],
  },
  id_portrait_clarity_detection: {
    label: "Portrait clarity",
    category: "User action required",
    description: "Detect if the face portrait is clear and in focus.",
    reasons: [
      {
        reason_slug: "portrait_not_clear",
        description: "The face portrait is unclear and out of focus.",
      },
    ],
  },
  id_portrait_detection: {
    label: "Portrait",
    category: "User action required",
    description: "Detect if there is a face portrait in the submission.",
    reasons: [
      {
        reason_slug: "portrait_not_found",
        description: "The submission has no face portrait.",
      },
    ],
  },
  id_public_figure_detection: {
    label: "Public figure",
    category: "Fraud",
    description:
      "Detect if the face portrait matches that of a known public figure.",
    reasons: [
      {
        reason_slug: "public_figure_detected",
        description:
          "The face portrait appears to match that of a known public figure.",
      },
      {
        reason_slug: "low_quality_portrait",
        description: "Portrait quality is too low for comparison.",
      },
      {
        reason_slug: "missing_face",
        description: "The ID has no face.",
      },
    ],
  },
  id_real_id_detection: {
    label: "REAL ID",
    category: "User action required",
    description: "Detect if a U.S. REAL ID.",
    reasons: [
      {
        reason_slug: "federal_limits_apply",
        description:
          "ID does not meet federal guidelines for REAL Enhanced IDs.",
      },
      {
        reason_slug: "federal_limits_undetected",
        description: "ID's compliance type could not be detected.",
      },
      {
        reason_slug: "invalid_barcode",
        description: "Barcode on ID could not be read.",
      },
      {
        reason_slug: "invalid_country",
        description: "REAL ID is not available for the given country.",
      },
      {
        reason_slug: "invalid_id_type",
        description: "ID type is not supported by REAL ID.",
      },
      {
        reason_slug: "no_barcode",
        description: "ID's barcode was not provided.",
      },
    ],
  },
  id_repeat_detection: {
    label: "Repeat",
    category: "Fraud",
    description:
      "Detect if the ID details and face portrait match that of a previously submitted ID on a different account within the last year.",
    reasons: [
      {
        reason_slug: "repeat",
        description:
          "The ID details and face portrait match that of a previously submitted ID on a different account.",
      },
      {
        reason_slug: "details_only_repeat",
        description:
          "The ID details match that of a previously submitted ID on a different account.",
      },
      {
        reason_slug: "not_enabled",
        description: "ID repeat detection is not enabled.",
      },
    ],
  },
  id_selfie_comparison: {
    label: "ID-to-Selfie comparison",
    category: "Fraud",
    description:
      "Compare if the face portrait on the ID and the selfie are different faces.",
    reasons: [
      {
        reason_slug: "face_mismatch",
        description:
          "The face portrait on the ID and the selfie appear to be different faces.",
      },
      {
        reason_slug: "no_selfie",
        description: "The submission has no selfie.",
      },
    ],
  },
  id_tamper_detection: {
    label: "ID image tampering",
    category: "Fraud",
    description: "Detect if the submission is tampered by a photo editor.",
    reasons: [
      {
        reason_slug: "portrait_tampering",
        description: "The submission appears to have a tampered portrait.",
      },
      {
        reason_slug: "exif_inconsistency",
        description: "The submission has inconsistencies in the file metadata.",
      },
      {
        reason_slug: "image_manipulated",
        description:
          "The submission appears to have been tampered by an editor.",
      },
    ],
  },
  id_unprocessable_submission_detection: {
    label: "Processable submission",
    category: "User action required",
    description: "Detect if the submission is unprocessable.",
    reasons: [
      {
        reason_slug: "age_below_limit",
        description:
          "The age detected in the portrait is below the processable age limit.",
      },
      {
        reason_slug: "submission_invalid",
        description: "The submission is unprocessable.",
      },
    ],
  },
  id_valid_dates_detection: {
    label: "Valid dates",
    category: "Fraud",
    description:
      "Detect if the dates on the ID are in violation of issuing authority rules. The check applies to US DLs and IDs, and CN IDs.",
    reasons: [
      {
        reason_slug: "issuer_rules_inconsistency",
        description:
          "The dates on the ID appear to be in violation of issuing authority rules.",
      },
      {
        reason_slug: "unknown_date_rules",
        description:
          "The ID is temporary or limited-term, so the valid date rules are unknown",
      },
    ],
  },
  id_video_quality_detection: {
    label: "BETA Video quality",
    category: "User action required",
    description: "Detect if the video is high enough quality.",
    reasons: [
      {
        reason_slug: "corrupt",
        description: "The video is corrupt.",
      },
      {
        reason_slug: "low_bit_rate",
        description: "The bit rate is too low.",
      },
      {
        reason_slug: "low_frame_rate",
        description: "The frame rate is too low.",
      },
      {
        reason_slug: "not_found",
        description: "There was no video uploaded.",
      },
      {
        reason_slug: "duration_too_short",
        description: "The duration of the video was too short.",
      },
    ],
  },
};
